import { inject, Injectable } from '@angular/core';
import { IDEA_API_SERVICE, IDropdownOption } from '@ideatech/ngx-ideatech';
import { TranslocoService } from '@jsverse/transloco';
import { catchError, map, Observable, of, tap, throwError } from 'rxjs';
import { ApiHolder } from '../classes/api-holder';
import { ApiActionOutcome } from '../enums/api-action-outcome.enum';
import { IAccountGetResponse } from '../interfaces/api-responses/account-get-response.interface';
import { ISignInRequest } from '../interfaces/api-requests/sign-in-request.interface';
import { ISignUpRequest } from '../interfaces/api-requests/sign-up-request.interface';
import { IRefreshTokenResponse } from '../interfaces/api-responses/refresh-token-response.interface';
import { ISignInResponse } from '../interfaces/api-responses/sign-in-response.interface';
import { AlertsService } from './alerts.service';

@Injectable({
    providedIn: 'root'
})
export class AuthenticationService {

    private readonly ideaApiService = inject(IDEA_API_SERVICE);
    private readonly translocoService = inject(TranslocoService);
    private readonly alertsService = inject(AlertsService);

    private static readonly IMAGE_EXPORT_FORMATS = ['PNG', 'JPEG'];
    private static readonly TECHNICAL_EXPORT_FORMATS = ['DXF', 'OBJ', 'STL'];

    private userCustomersLookup?: IDropdownOption[];
    private isUserAdministrator = false;
    private isUserTechnicalOffice = false;
    private isUserBackOffice = false;

    public ping(): Observable<boolean> {
        return this.ideaApiService.request<IAccountGetResponse>(ApiHolder.account.get()).pipe(
            map(result => {
                this.isUserAdministrator = result.data?.isAdministrator ?? false;
                this.isUserTechnicalOffice = result.data?.isTechnicalOffice ?? false;
                this.isUserBackOffice = result.data?.isBackOffice ?? false;
                return true;
            }),
            catchError(error => of(false))
        );
    }

    public isAdministrator(): boolean {
        return this.isUserAdministrator;
    }

    public isTechnicalOffice(): boolean {
        return this.isUserTechnicalOffice;
    }

    public isBackOffice(): boolean {
        return this.isUserBackOffice;
    }

    private getAllowedExportFormats(): string[] {
        if (this.isUserAdministrator || this.isUserTechnicalOffice) {
            return [...AuthenticationService.IMAGE_EXPORT_FORMATS, ...AuthenticationService.TECHNICAL_EXPORT_FORMATS];
        }
        return [...AuthenticationService.IMAGE_EXPORT_FORMATS];
    }

    public canDownloadFormat(format: string): boolean {
        return this.getAllowedExportFormats().includes(format.toUpperCase());
    }

    public signIn(requestBody: ISignInRequest): Observable<ISignInResponse | null> {
        this.clearStoredTokens();
        this.clearUserData();
        return this.ideaApiService.request<ISignInResponse>(ApiHolder.account.signin(requestBody)).pipe(
            map(resultSignIn => resultSignIn.data),
            tap(resultSignIn => {
                if (resultSignIn && resultSignIn.outcome === ApiActionOutcome.SUCCESS && resultSignIn.accessToken && resultSignIn.refreshToken) {
                    this.setTokens(resultSignIn.accessToken, resultSignIn.refreshToken);
                }
            })
        );
    }

    public signUp(requestBody: ISignUpRequest): Observable<void | null> {
        this.clearStoredTokens();
        this.clearUserData();
        return this.ideaApiService.request<void>(ApiHolder.account.signup(requestBody)).pipe(
            map(resultSignUp => resultSignUp.data)
        );
    }

    public refreshToken(): Observable<IRefreshTokenResponse | null> {
        const accessToken = localStorage.getItem("accessToken");
        const refreshToken = localStorage.getItem("refreshToken");
        if (!accessToken || !refreshToken) {
            return throwError(() => {});
        } else {
            return this.ideaApiService.request<IRefreshTokenResponse>(ApiHolder.account.refreshToken({ accessToken, refreshToken })).pipe(
                map(resultRefreshToken => resultRefreshToken.data),
                tap(resultRefreshToken => {
                    if (resultRefreshToken && resultRefreshToken.accessToken && resultRefreshToken.refreshToken) {
                        this.setTokens(resultRefreshToken.accessToken, resultRefreshToken.refreshToken);
                    }
                })
            )
        }
    }

    public logout(): void {
        this.clearStoredTokens();
        this.clearUserData();
    }

    public getUserCustomersLookup(): Observable<IDropdownOption[]> {
        if (this.userCustomersLookup) {
            return of(this.userCustomersLookup.slice());
        } else {
            return this.ideaApiService.request<IDropdownOption[]>(ApiHolder.account.getCustomersLookup()).pipe(
                catchError(error => {
                    this.showCustomersNotFoundError();
                    return throwError(() => error);
                }),
                map(customersLookupResult => {
                    this.userCustomersLookup = customersLookupResult.data!;
                    return this.userCustomersLookup.slice();
                })
            );
        }
    }

    private clearStoredTokens(): void {
        localStorage.removeItem("accessToken");
        localStorage.removeItem("refreshToken");
    }

    private setTokens(accessToken: string, refreshToken: string): void {
        localStorage.setItem("accessToken", accessToken);
        localStorage.setItem("refreshToken", refreshToken);
    }

    private clearUserData(): void {
        delete this.userCustomersLookup;
        this.isUserAdministrator = false;
        this.isUserTechnicalOffice = false;
        this.isUserBackOffice = false;
    }

    private showCustomersNotFoundError(): void {
        this.alertsService .open({
            type: 'danger',
            delay: 5000,
            dismissable: true,
            message: this.translocoService.translate("IDS_NO_CUSTOMER_FOUND")
        });
    }
}
